import React, { useState } from 'react';
import { Box, Avatar, IconButton, Badge, Typography, Paper } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CONTEXTS } from 'figshelf-modules';
import { useChatPopup } from '../../contexts/ChatPopupContext';

const { useMessages } = CONTEXTS;

/**
 * Bottom bar showing minimized chat windows
 * Similar to Facebook's minimized chat tabs
 */
const ChatPopupBar = () => {
  const { openChats, getMinimizedChats, restoreChat, closeChat, openChat, isChatOpen } = useChatPopup();
  const { conversations, getUnreadCount, user, conversationUnreadCount, markConversationAsReadLocal } = useMessages();
  const minimizedChats = getMinimizedChats();
  const [hoveredChatId, setHoveredChatId] = useState(null);

  // Get conversations with unread messages that are not already open
  const unreadConversationIds = Object.entries(conversationUnreadCount || {})
    .filter(([_, count]) => count > 0)
    .map(([id]) => id)
    .filter(id => !isChatOpen(id));

  // Combine minimized chats with unread-only conversations
  const allChatsToShow = [
    ...minimizedChats,
    ...unreadConversationIds.map(id => ({ conversationId: id, isMinimized: true, isUnreadOnly: true }))
  ];

  const getConversationData = (conversationId) => {
    const conversation = conversations?.find(c => c.id === conversationId);
    const unreadCount = typeof getUnreadCount === 'function' ? getUnreadCount(conversationId) : 0;
    if (!conversation) return { name: 'Loading...', avatar: null, unreadCount };

    let name = conversation.name || 'Conversation';
    let avatar = conversation.avatar;

    if (conversation.type === 'direct' && conversation.participants?.length > 0) {
      const otherParticipant = conversation.participants.find(
        p => p.user && p.user.id !== (conversation.currentUserId || user?.id)
      );

      if (otherParticipant) {
        name = otherParticipant.nickname ||
               otherParticipant.user?.profile?.displayName ||
               otherParticipant.user?.username ||
               'User';

        if (otherParticipant?.user?.profile?.media) {
          const avatarMedia = otherParticipant.user.profile.media.find(m => m.type === 'avatar');
          avatar = avatarMedia?.url;
        }
      }
    }

    return {
      name,
      avatar,
      unreadCount,
    };
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 100,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        zIndex: 1299, // Just below active chat popups
        pointerEvents: 'none', // Allow clicks through empty space,
      }}
    >
      {allChatsToShow.map((chat) => {
        const { name, avatar, unreadCount } = getConversationData(chat.conversationId);

        const handleClick = () => {
          if (chat.isUnreadOnly && !isChatOpen(chat.conversationId)) {
            openChat(chat.conversationId);
          } else {
            restoreChat(chat.conversationId);
          }
        };

        const handleClose = (e) => {
          e.stopPropagation();
          if (chat.isUnreadOnly) {
            // For unread-only items, clear the unread count to dismiss the notification
            if (typeof markConversationAsReadLocal === 'function') {
              markConversationAsReadLocal(chat.conversationId);
            }
          } else {
            closeChat(chat.conversationId);
          }
        };

        return (
          <Paper
            key={chat.conversationId}
            elevation={4}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: 'white',
              pointerEvents: 'auto',
              transition: 'all 0.2s',
              position: 'relative',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                transform: 'translateY(-2px)',
              },
            }}
            onClick={handleClick}
            onMouseEnter={() => setHoveredChatId(chat.conversationId)}
            onMouseLeave={() => setHoveredChatId(null)}
          >
              <Badge
                badgeContent={unreadCount}
                color="error"
                overlap="circular"
                sx={{ mr: 1.5, zIndex:1000 }}
              >
                <Avatar
                  src={avatar}
                  sx={{ width: '60px', height: '60px' }}
                >
                  {name[0]}
                </Avatar>
              </Badge>
            
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: -10,
                  top: -10,
                  zIndex: 1300,
                  background: '#fff',
                  opacity: hoveredChatId === chat.conversationId ? 1 : 0,
                  transition: 'opacity 0.2s',
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
          </Paper>
        );
      })}
    </Box>
  );
};

export default ChatPopupBar;
